import { ComponentType, memo, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldAttributes, useField, useFormikContext } from 'formik';

import { TextInput } from 'components/inputs';
import { IBasicInputProps } from 'types/components/inputs';
import IconSvg from 'components/icons/index';
import { Icons } from 'types/components/icons';

import styles from './FormField.module.scss';

interface IProps extends FieldAttributes<any> {
  name: string;
  label?: ReactNode;
  placeholder?: string;
  inputComponent?: ComponentType<IBasicInputProps<any> & any>;
  disabled?: boolean;
  required?: boolean;
  validateField?: boolean;
  fieldlabel?: string;
  icon?: Icons;
  defaultValue?: string | number | boolean;
  isDisableDates?: boolean;
  isNeededCustomStyle?: boolean;
}

const FormField = ({
  name,
  inputComponent: InputComponent = TextInput,
  disabled = false,
  required = false,
  validateField = false,
  isDisableDates = false,
  label,
  placeholder,
  icon,
  fieldlabel,
  defaultValue,
  error,
  isNeededCustomStyle = true,
  ...rest
}: IProps) => {
  const { t } = useTranslation();
  const { isSubmitting, setFieldTouched, validateField: validateFormField } = useFormikContext();
  const [field, meta, helpers] = useField(name);
  const errorMessage = error ? error : typeof meta.error === 'string' ? t(meta.error!) : meta.error;

  useEffect(() => {
    if (defaultValue) {
      helpers.setValue(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = useCallback(
    (value: unknown) => {
      //TODO: need to find way to improove async validation
      helpers.setValue(value);
      setTimeout(() => {
        setFieldTouched(field.name);
        if (validateField) {
          validateFormField(field.name);
        }
      }, 0);
    },
    [field.name, helpers, setFieldTouched, validateField, validateFormField],
  );

  const handleBlur = useCallback(() => {
    helpers.setTouched(true);
  }, [helpers]);

  return (
    <div className={styles.formFieldWrapper}>
      {icon && (
        <div className={styles.icon}>
          <IconSvg icon={icon} />
        </div>
      )}
      <Field
        component={InputComponent}
        name={name}
        error={errorMessage}
        icon={icon}
        label={typeof label === 'string' && required ? `${label}*` : label}
        placeholder={placeholder && required ? `${placeholder}*` : placeholder}
        disabled={isSubmitting || disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        fieldlabel={fieldlabel}
        value={field.value}
        autoComplete='new-password'
        isDisableDates={isDisableDates}
        className={isNeededCustomStyle ? styles.field : undefined}
        {...rest}
      />
    </div>
  );
};

export default memo(FormField);
